import type { WidgetSource, WidgetView } from './api';

/**
 * Bibliotheek met kant-en-klare rijen (zoals Xperience): TMDB-selecties per thema, en startpakketten
 * die in één keer een hele set rijen toevoegen. Alles via TMDB Discover, dus licht en zonder extra sleutels.
 */

export interface LibRow { id: string; title: string; source: WidgetSource; view?: WidgetView }
export interface LibCategory { id: string; title: string; rows: LibRow[] }

const d = (media: 'movie' | 'tv', params: Record<string, string>, sortBy = 'popularity.desc'): WidgetSource => ({ kind: 'tmdb', list: 'discover', media, params, sortBy });
const decade = (from: number) => d('movie', { 'primary_release_date.gte': `${from}-01-01`, 'primary_release_date.lte': `${from + 9}-12-31`, 'vote_count.gte': '800' }, 'vote_average.desc');
const studio = (id: number) => d('movie', { with_companies: String(id), 'vote_count.gte': '50' });
const network = (id: number) => d('tv', { with_networks: String(id), 'vote_count.gte': '50' });
const lang = (code: string, extra: Record<string, string> = {}, media: 'movie' | 'tv' = 'movie') => d(media, { with_original_language: code, 'vote_count.gte': '150', ...extra }, 'vote_average.desc');

export const ROW_LIBRARY: LibCategory[] = [
  {
    id: 'mood', title: 'Stemming', rows: [
      { id: 'feelgood', title: 'Feelgood', source: d('movie', { with_genres: '35|10751', 'vote_average.gte': '6.8', 'vote_count.gte': '500', without_genres: '27,53' }) },
      { id: 'tension', title: 'Op het puntje van je stoel', source: d('movie', { with_genres: '53,9648', 'vote_average.gte': '7', 'vote_count.gte': '800' }) },
      { id: 'tearjerker', title: 'Zakdoekjes klaar', source: d('movie', { with_genres: '18,10749', 'vote_average.gte': '7.3', 'vote_count.gte': '800' }) },
      { id: 'short', title: 'Kort en goed (onder 95 min)', source: d('movie', { 'with_runtime.lte': '95', 'vote_average.gte': '7', 'vote_count.gte': '800' }) },
      { id: 'epic', title: 'Lange epics (2,5 uur+)', source: d('movie', { 'with_runtime.gte': '150', 'vote_average.gte': '7.5', 'vote_count.gte': '1500' }) },
      { id: 'family', title: 'Familieavond', source: d('movie', { with_genres: '10751', 'vote_average.gte': '6.8', 'vote_count.gte': '800' }) },
      { id: 'mindbend', title: 'Breinbrekers', source: d('movie', { with_genres: '878,9648', 'vote_average.gte': '7', 'vote_count.gte': '800' }) },
    ],
  },
  {
    id: 'best', title: 'Het beste', rows: [
      { id: 'alltime', title: 'Hoogst gewaardeerd aller tijden', source: d('movie', { 'vote_count.gte': '8000' }, 'vote_average.desc'), view: 'top10' },
      { id: 'alltime-tv', title: 'Beste series aller tijden', source: d('tv', { 'vote_count.gte': '3000' }, 'vote_average.desc'), view: 'top10' },
      { id: 'recent-best', title: 'Beste films van de laatste 2 jaar', source: d('movie', { 'primary_release_date.gte': `${new Date().getFullYear() - 2}-01-01`, 'vote_count.gte': '1000' }, 'vote_average.desc') },
      { id: 'blockbusters', title: 'Grootste kaskrakers', source: d('movie', { 'vote_count.gte': '500' }, 'revenue.desc') },
    ],
  },
  {
    id: 'decades', title: 'Decennia', rows: [
      { id: '70s', title: 'Klassiekers uit de jaren 70', source: decade(1970) },
      { id: '80s', title: 'Jaren 80', source: decade(1980) },
      { id: '90s', title: 'Jaren 90', source: decade(1990) },
      { id: '00s', title: 'Jaren 00', source: decade(2000) },
      { id: '10s', title: 'Jaren 10', source: decade(2010) },
    ],
  },
  {
    id: 'genres', title: 'Genres', rows: [
      { id: 'actcom', title: 'Actiekomedies', source: d('movie', { with_genres: '28,35', 'vote_count.gte': '300' }) },
      { id: 'scifi-thr', title: 'Sciencefiction-thrillers', source: d('movie', { with_genres: '878,53', 'vote_count.gte': '300' }) },
      { id: 'romcom', title: 'Romantische komedies', source: d('movie', { with_genres: '10749,35', 'vote_count.gte': '300' }) },
      { id: 'horcom', title: 'Horrorkomedies', source: d('movie', { with_genres: '27,35', 'vote_count.gte': '200' }) },
      { id: 'crimedrama', title: 'Misdaaddrama', source: d('movie', { with_genres: '80,18', 'vote_count.gte': '500' }) },
      { id: 'war', title: 'Oorlogsfilms', source: d('movie', { with_genres: '10752', 'vote_count.gte': '300' }, 'vote_average.desc') },
      { id: 'western', title: 'Westerns', source: d('movie', { with_genres: '37', 'vote_count.gte': '200' }, 'vote_average.desc') },
      { id: 'docs', title: 'Documentaires', source: d('movie', { with_genres: '99', 'vote_count.gte': '100' }, 'vote_average.desc') },
      { id: 'fantasy', title: 'Fantasy-avonturen', source: d('movie', { with_genres: '14,12', 'vote_count.gte': '500' }) },
      { id: 'animation-adult', title: 'Animatie (niet alleen voor kinderen)', source: d('movie', { with_genres: '16', without_genres: '10751', 'vote_count.gte': '200' }, 'vote_average.desc') },
    ],
  },
  {
    id: 'world', title: 'Wereldcinema', rows: [
      { id: 'nl', title: 'Nederlandse films', source: lang('nl', { 'vote_count.gte': '30' }) },
      { id: 'ko', title: 'Koreaanse films', source: lang('ko') },
      { id: 'ja', title: 'Japanse films', source: lang('ja') },
      { id: 'fr', title: 'Franse films', source: lang('fr') },
      { id: 'es', title: 'Spaanstalige films', source: lang('es') },
      { id: 'it', title: 'Italiaanse films', source: lang('it') },
      { id: 'de', title: 'Duitse films', source: lang('de') },
      { id: 'scandi', title: 'Scandinavische films', source: lang('sv|da|no') },
      { id: 'hi', title: 'Indiase films', source: lang('hi|ta|te') },
      { id: 'kdrama', title: 'Koreaanse series', source: lang('ko', { with_genres: '18' }, 'tv') },
      { id: 'anime', title: 'Anime-series', source: lang('ja', { with_genres: '16' }, 'tv') },
      { id: 'nordic-noir', title: 'Nordic noir (series)', source: lang('sv|da|no', { with_genres: '80' }, 'tv') },
    ],
  },
  {
    id: 'studios', title: "Studio's", rows: [
      { id: 'a24', title: 'A24', source: studio(41077) },
      { id: 'pixar', title: 'Pixar', source: studio(3) },
      { id: 'ghibli', title: 'Studio Ghibli', source: studio(10342) },
      { id: 'marvel', title: 'Marvel Studios', source: studio(420) },
      { id: 'blumhouse', title: 'Blumhouse (horror)', source: studio(3172) },
      { id: 'lucasfilm', title: 'Lucasfilm', source: studio(1) },
      { id: 'dreamworks', title: 'DreamWorks Animation', source: studio(521) },
      { id: 'aardman', title: 'Aardman', source: studio(297) },
    ],
  },
  {
    id: 'networks', title: 'Series per dienst', rows: [
      { id: 'hbo', title: 'HBO', source: network(49) },
      { id: 'netflix', title: 'Netflix Originals', source: network(213) },
      { id: 'appletv', title: 'Apple TV+', source: network(2552) },
      { id: 'disney', title: 'Disney+', source: network(2739) },
      { id: 'prime', title: 'Prime Video', source: network(1024) },
      { id: 'bbc', title: 'BBC', source: network(4) },
      { id: 'fx', title: 'FX', source: network(88) },
    ],
  },
];

/** Startpakketten: een hele set rijen in één keer. */
export const ROW_PRESETS: { id: string; title: string; hint: string; rows: string[] }[] = [
  { id: 'cinema', title: 'Filmavond', hint: 'Het beste, kaskrakers, spanning en feelgood', rows: ['alltime', 'recent-best', 'blockbusters', 'tension', 'feelgood'] },
  { id: 'world', title: 'Wereldcinema', hint: 'Korea, Japan, Frankrijk, Scandinavië en Nederland', rows: ['ko', 'ja', 'fr', 'scandi', 'nl'] },
  { id: 'series', title: 'Seriejunkie', hint: 'Beste series en de grote diensten', rows: ['alltime-tv', 'hbo', 'appletv', 'netflix', 'kdrama'] },
  { id: 'indie', title: 'Filmliefhebber', hint: "A24, Ghibli, klassiekers en documentaires", rows: ['a24', 'ghibli', '70s', '80s', 'docs'] },
  { id: 'kids', title: 'Familie', hint: 'Pixar, DreamWorks, Ghibli en familieavond', rows: ['family', 'pixar', 'dreamworks', 'ghibli', 'aardman'] },
];

export function libRow(id: string): LibRow | undefined {
  for (const c of ROW_LIBRARY) {
    const r = c.rows.find((x) => x.id === id);
    if (r) return r;
  }
  return undefined;
}
